import { useCallback, useEffect, useState } from "react";
import { Newspaper, Plus, X, Sparkles, Loader2, History, Shield, ScrollText, Globe, CheckCircle2, ImagePlus, Eye } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

function Chip({ status }) {
  const cls = status === "Published" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500";
  return <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${cls}`}>{status}</span>;
}

function ImageLibraryPicker({ onPick, onClose }) {
  const [imgs, setImgs] = useState(null);
  useEffect(() => { api.get("/ai-images/overview").then(({ data }) => setImgs(data.images)).catch(() => setImgs([])); }, []);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="image-library-picker">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between"><h3 className="font-head text-lg font-extrabold text-slate-900">Insert an image</h3><button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        {!imgs ? <Loader2 size={18} className="mx-auto my-6 animate-spin text-slate-300" /> : imgs.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-slate-400">No images yet. Generate some in the AI Image Studio first.</p>
        ) : (
          <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3">
            {imgs.map((im) => (
              <button key={im.id} onClick={() => onPick(im)} data-testid="picker-image" className="group overflow-hidden rounded-lg border border-slate-200 hover:border-[#b0862f]">
                <img src={`${API}/ai-images/public/${im.id}`} alt={im.prompt} className="aspect-square w-full object-cover" />
                <div className="truncate p-1.5 text-left text-[10px] text-slate-500" title={im.prompt}>{im.prompt}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Editor({ page, permission, onClose, onSaved }) {
  const [title, setTitle] = useState(page.title);
  const [body, setBody] = useState(page.body);
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [versions, setVersions] = useState(null);
  const [preview, setPreview] = useState(false);
  const [imgPicker, setImgPicker] = useState(false);

  const loadVersions = () => api.get(`/content/pages/${page.id}/versions`).then(({ data }) => setVersions(data.versions));

  const insertImage = (im) => {
    const alt = (im.prompt || "Image").replace(/]/g, "").replace(/\n/g, " ").slice(0, 120);
    setBody((b) => `${b}\n\n![${alt}](/api/ai-images/public/${im.id})\n`);
    setImgPicker(false);
    toast.success("Image inserted — Save version to keep it");
  };

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch(`/content/pages/${page.id}`, { title, body }); toast.success("Saved as new version"); onSaved(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/content/pages/${page.id}/publish`); toast.success("Published"); onSaved(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed QA."); } finally { setBusy(false); }
  };
  const runAi = async () => {
    if (!instruction.trim()) return toast.error("Tell the AI what to write.");
    setBusy(true);
    try {
      const { data } = await api.post(`/content/pages/${page.id}/ai-draft`, { instruction });
      setBody(data.page.body); setTitle(data.page.title);
      toast.success(data.auto_published ? "AI wrote and published this page" : "AI drafted a new version — review and publish");
      setAiOpen(false); setInstruction(""); onSaved(data.page);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed."); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="content-editor">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <div className="flex items-center gap-2"><Globe size={16} className="text-slate-400" /><span className="font-mono text-[11px] text-slate-400">/{page.slug}</span><Chip status={page.status} /></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="editor-title"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 font-head text-[16px] font-bold outline-none focus:border-[#1a5fd0]" />
          {preview ? (
            <div className="prose prose-sm max-w-none rounded-lg border border-slate-200 p-4" data-testid="editor-preview">
              <ReactMarkdown components={{ img: ({ node, ...props }) => <img {...props} alt={props.alt || ""} className="my-2 max-w-full rounded-lg" /> }}>{body || "_Nothing to preview yet._"}</ReactMarkdown>
            </div>
          ) : (
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} data-testid="editor-body"
              className="w-full rounded-lg border border-slate-200 p-3 font-mono text-[12px] leading-relaxed outline-none focus:border-[#1a5fd0]" placeholder="Page content in Markdown…" />
          )}
          {aiOpen && (
            <div className="rounded-xl border border-[#b0862f]/30 bg-[#b0862f]/5 p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-bold text-[#b0862f]"><Sparkles size={13} /> Draft with AI ({permission === "publish" ? "will auto-publish" : "creates a draft to approve"})</div>
              <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2} data-testid="ai-instruction"
                className="w-full rounded-lg border border-slate-200 p-2 text-[13px] outline-none focus:border-[#b0862f]" placeholder="e.g. Rewrite the hero to focus on funding readiness, add a 3-step how-it-works section." />
              <button onClick={runAi} disabled={busy} data-testid="run-ai-draft" className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#b0862f] px-4 py-2 text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate
              </button>
            </div>
          )}
          {versions && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Version history</div>
              <div className="space-y-1.5">
                {versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-700">v{v.version} · {v.note}</span>
                    <span className="font-mono text-[10px] text-slate-400">{v.author} · {new Date(v.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 p-4">
          <button onClick={save} disabled={busy} data-testid="save-page-btn" className="rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">Save version</button>
          <button onClick={publish} disabled={busy} data-testid="publish-page-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-bold text-white hover:bg-emerald-700 disabled:opacity-60"><CheckCircle2 size={14} /> Publish</button>
          {permission !== "off" && <button onClick={() => setAiOpen((v) => !v)} data-testid="toggle-ai-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-[#b0862f]/40 px-4 py-2 text-[13px] font-bold text-[#b0862f]"><Sparkles size={14} /> Draft with AI</button>}
          <button onClick={() => setImgPicker(true)} data-testid="insert-image-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-bold text-slate-700 hover:border-slate-300"><ImagePlus size={14} /> Insert image</button>
          <button onClick={() => setPreview((v) => !v)} data-testid="toggle-preview-btn" className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-[13px] font-bold ${preview ? "border-[#1a5fd0] text-[#1a5fd0]" : "border-slate-200 text-slate-700"}`}><Eye size={14} /> {preview ? "Editing" : "Preview"}</button>
          <button onClick={loadVersions} className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 hover:text-slate-800"><History size={14} /> History</button>
        </div>
      </div>
      {imgPicker && <ImageLibraryPicker onPick={insertImage} onClose={() => setImgPicker(false)} />}
    </div>
  );
}

export default function ContentStudio() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("pages");
  const [editing, setEditing] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", slug: "", body: "" });
  const load = useCallback(() => { api.get("/content/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { pages, audit, ai_permission, stats, permissions, permission_labels } = data;

  const createPage = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    try { await api.post("/content/pages", form); toast.success("Page created"); setNewOpen(false); setForm({ title: "", slug: "", body: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create page."); }
  };
  const setPermission = async (perm) => { try { await api.patch("/content/permissions", { ai_permission: perm }); toast.success("AI permission updated"); load(); } catch { toast.error("Could not update."); } };

  const TABS = [["pages", "Pages", Newspaper], ["permissions", "AI Permissions", Shield], ["audit", "Audit Log", ScrollText]];

  return (
    <div className="animate-fade-up space-y-6" data-testid="content-studio-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Newspaper size={22} className="text-[#b0862f]" /> Content Studio</h1>
        <p className="mt-1 text-[13px] text-slate-500">Website pages with full version history and AI publishing within the permissions you set.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Pages" value={stats.pages} tone="slate" />
        <Stat label="Published" value={stats.published} tone="green" />
        <Stat label="Drafts" value={stats.drafts} tone="blue" />
      </div>

      <div className="flex gap-5 border-b border-slate-200">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`content-tab-${id}`}
            className={`flex items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${tab === id ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "pages" && (
        <div className="space-y-3" data-testid="content-pages">
          <div className="flex justify-end">
            <button onClick={() => setNewOpen(true)} data-testid="new-page-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3.5 py-2 text-[13px] font-bold text-white hover:bg-[#1550b8]"><Plus size={15} /> New page</button>
          </div>
          {pages.map((p) => (
            <Card key={p.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 hover:border-slate-300" data-testid="page-row" onClick={() => setEditing(p)}>
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="font-head text-[15px] font-bold text-slate-900">{p.title}</span><Chip status={p.status} /></div>
                <div className="mt-0.5 font-mono text-[11px] text-slate-400">/{p.slug} · v{p.current_version} · updated by {p.updated_by}</div>
              </div>
              <span className="shrink-0 text-[12px] font-semibold text-[#1a5fd0]">Edit →</span>
            </Card>
          ))}
        </div>
      )}

      {tab === "permissions" && (
        <div className="space-y-2" data-testid="content-permissions">
          <p className="text-[13px] text-slate-500">Decide how much your Capital Crew can do with website pages. Publishing always runs a QA check.</p>
          {permissions.map((perm) => (
            <Card key={perm} className={`flex cursor-pointer items-center justify-between gap-3 p-4 ${ai_permission === perm ? "border-[#080d1a] ring-1 ring-[#080d1a]" : ""}`} data-testid={`permission-${perm}`} onClick={() => setPermission(perm)}>
              <div>
                <div className="font-head text-[14px] font-bold capitalize text-slate-900">{perm === "off" ? "Off" : perm === "draft" ? "Draft only" : "Full publish"}</div>
                <div className="text-[12px] text-slate-500">{permission_labels[perm]}</div>
              </div>
              {ai_permission === perm && <CheckCircle2 size={18} className="text-emerald-500" />}
            </Card>
          ))}
        </div>
      )}

      {tab === "audit" && (
        <Card className="p-4" data-testid="content-audit">
          {audit.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No activity yet.</div> : (
            <div className="space-y-2">
              {audit.map((a) => (
                <div key={a.id} className="flex items-center justify-between border-b border-slate-50 py-2 text-[13px] last:border-0">
                  <span className="text-slate-700"><b>{a.actor}</b> · {a.action} <span className="text-slate-400">{a.page !== "—" ? `“${a.page}”` : ""} {a.detail}</span></span>
                  <span className="font-mono text-[10px] text-slate-400">{new Date(a.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {editing && <Editor page={editing} permission={ai_permission} onClose={() => { setEditing(null); load(); }} onSaved={(pg) => setEditing(pg)} />}

      {newOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-page-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setNewOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-head text-lg font-extrabold text-slate-900">New page</h3><button onClick={() => setNewOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
            <div className="space-y-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-page-title" placeholder="Page title" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px] outline-none focus:border-[#1a5fd0]" />
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="slug (optional)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} placeholder="Content (Markdown)…" className="w-full rounded-lg border border-slate-200 p-3 font-mono text-[12px] outline-none focus:border-[#1a5fd0]" />
            </div>
            <button onClick={createPage} data-testid="create-page-btn" className="mt-4 w-full rounded-lg bg-[#1a5fd0] py-2.5 text-[14px] font-bold text-white hover:bg-[#1550b8]">Create page</button>
          </div>
        </div>
      )}
    </div>
  );
}
